import { useState, useEffect } from "react";
import { Lock, Envelope, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_collab-space-83/artifacts/cz1qezzm_LOGO%201%20PNG%20FILE%20%281%29.png";

const TypeWriter = ({ text, delay = 80 }) => {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) { setDisplayed(text.slice(0, i + 1)); i++; }
      else clearInterval(interval);
    }, delay);
    return () => clearInterval(interval);
  }, [text, delay]);
  return <>{displayed}<span className="login-cursor">|</span></>;
};

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password, remember_me: rememberMe }, { withCredentials: true });
      const data = res.data;
      if (data.session_token) {
        onLogin(data.user, data.session_token, data.must_change_password);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed";
      toast.error(msg);
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
      toast.success("Check your email for a reset link!");
    } catch {
      toast.error("Something went wrong. Try again.");
    }
    setForgotLoading(false);
  };

  return (
    <div className="login-scene">
      {/* Animated mesh gradient orbs */}
      <div className="login-orbs" aria-hidden="true">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      {/* Noise overlay */}
      <div className="login-noise" aria-hidden="true" />

      <div className="w-full max-w-sm mx-4 relative z-10">
        {/* Flaming head mascot */}
        <div className="text-center mb-8">
          <div className="login-fire-container">
            {/* Ember particles floating up */}
            <div className="login-embers" aria-hidden="true">
              {[...Array(20)].map((_, i) => (
                <div key={i} className={`login-ember login-ember-${i}`} />
              ))}
            </div>

            {/* Flame tongues — all around */}
            <div className="login-flames" aria-hidden="true">
              {[...Array(18)].map((_, i) => (
                <div key={i} className={`login-flame login-flame-${i}`} />
              ))}
            </div>

            {/* Heat haze */}
            <div className="login-heat-haze" aria-hidden="true" />

            {/* Warm underglow */}
            <div className="login-fire-glow" />

            {/* Head icon — no ring */}
            <div className="login-head-circle login-float">
              <img src="/skyeai_head.png" alt="" className="login-head-img" draggable={false} />
            </div>
          </div>

          {/* Full logo replacing text */}
          <img
            src={LOGO_URL}
            alt="SkyeAI"
            className="login-brand-logo login-title"
            draggable={false}
          />

          <p className="login-subtitle text-sm text-[#71717A] mt-2 h-5">
            <TypeWriter text="Command Center" delay={70} />
          </p>
        </div>

        {/* Glass card */}
        <form
          onSubmit={handleSubmit}
          className={`login-glass-card rounded-2xl p-6 space-y-5 transition-all duration-500 ${focused ? "login-card-glow" : ""}`}
          data-testid="login-form"
        >
          <div>
            <label className="block text-[10px] font-bold text-[#A1A1AA]/70 mb-1.5 uppercase tracking-[0.2em]">Email</label>
            <div className="relative">
              <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com"
                className="login-input w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-[#71717A] outline-none transition-all"
                data-testid="login-email" autoComplete="email" autoFocus
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-[#A1A1AA]/70 mb-1.5 uppercase tracking-[0.2em]">Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password"
                className="login-input w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-[#71717A] outline-none transition-all"
                data-testid="login-password" autoComplete="current-password"
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A93] hover:text-[#A1A1AA] transition-all" tabIndex={-1}>
                {showPass ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none -mt-1" data-testid="remember-me-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="login-remember-checkbox"
              data-testid="remember-me-checkbox"
            />
            <span className="text-[11px] text-[#A1A1AA]/80">Keep me signed in for 30 days</span>
          </label>

          <button type="submit" disabled={loading || !email.trim() || !password.trim()} data-testid="login-submit"
            className="login-submit-btn login-shimmer-btn w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-white rounded-xl transition-all duration-300 disabled:opacity-30 overflow-hidden relative"
            style={{ background: "linear-gradient(135deg, #9D32F3, #3B40D4)" }}>
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="login-spinner" />
                Signing in...
              </span>
            ) : (
              <><span>Sign In</span><ArrowRight size={14} weight="bold" className="login-arrow" /></>
            )}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotSent(false); }}
              className="text-[11px] text-[#8A8A93] hover:text-[#9D32F3] transition-all"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>
        </form>

        {/* Forgot Password Modal */}
        {showForgot && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}>
            <div className="w-full max-w-sm mx-4 rounded-2xl p-6 border border-white/[0.08]" style={{ background: "rgba(10,10,10,0.95)" }} data-testid="forgot-modal">
              {forgotSent ? (
                <div className="text-center space-y-4">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#10b981]/10 flex items-center justify-center">
                    <Envelope size={22} className="text-[#10b981]" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Check Your Email</h3>
                  <p className="text-xs text-[#8A8A93] leading-relaxed">
                    If an account exists for <span className="text-white font-semibold">{forgotEmail}</span>, we sent a password reset link. Check your inbox (and spam folder).
                  </p>
                  <button
                    onClick={() => setShowForgot(false)}
                    className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                    style={{ background: "linear-gradient(135deg, #9D32F3, #3B40D4)" }}
                    data-testid="forgot-done-btn"
                  >
                    Back to Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4">
                  <div className="text-center mb-2">
                    <h3 className="text-lg font-bold text-white">Reset Password</h3>
                    <p className="text-xs text-[#71717A] mt-1">Enter your email and we'll send a reset link</p>
                  </div>
                  <div className="relative">
                    <Envelope size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] text-white placeholder-[#52525B] outline-none focus:border-[#9D32F3]/40 transition-all"
                      data-testid="forgot-email-input"
                      autoFocus
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading || !forgotEmail.trim()}
                    className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40"
                    style={{ background: "linear-gradient(135deg, #9D32F3, #3B40D4)" }}
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? "Sending..." : "Send Reset Link"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgot(false)}
                    className="w-full py-2 text-xs text-[#71717A] hover:text-white transition-all"
                  >
                    Cancel
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        <p className="login-footer text-center text-[10px] text-[#52525B] mt-8 tracking-[0.15em] uppercase">SkyeAI Command Center</p>
      </div>
    </div>
  );
}
